import { Category, ColumnAttribute, ColumnFamily, ComplimentaryFieldMap, InCompatibleField } from '@flexible-reports/store/metadata.types';
import {
  AttributionModel,
  CompatibilityType,
  DimensionFieldDetails,
  FieldType,
  FlexibleMetadata,
  MetricFieldDetails,
  ReportingField
} from '@flexible-reports/store/api/metadata/types';
import { computeTransitiveFields } from '@flexible-reports/store/api/metadata/transformers/compute-transitive-fields';

const DEFAULT_DISPLAY_ORDER = 99999;
interface BaseFamily {
  id?: string;
  name?: string;
  displayOrder?: number;
}

/**
 * Fetches category and family from DimensionFieldDetails.
 */
const getDimDetails = (dimFieldDetails: DimensionFieldDetails) => {
  const dimensionCategory = dimFieldDetails?.dimensionCategory;
  const dimensionFamily = dimFieldDetails?.dimension;
  return {
    enumValues: dimFieldDetails?.enumValues,
    dataRetentionPeriod: dimFieldDetails?.dataRetentionPeriod,
    dimCategory: {
      id: dimensionCategory.dimensionCategory,
      name: dimensionCategory.name,
      displayOrder: dimensionCategory.displayOrder
    },
    dimFamily: {
      id: dimensionFamily?.dimension,
      name: dimensionFamily?.name,
      displayOrder: dimensionFamily?.displayOrder
    }
  };
};

/**
 * Fetches category and family from MetricFieldDetails.
 */
const getMetricDetails = (metricFieldDetails: MetricFieldDetails) => {
  const metricCategory = metricFieldDetails?.metricCategory;
  const metricFamily = metricFieldDetails?.metricFamily;

  return {
    metricCategory: {
      id: metricCategory.metricCategory,
      name: metricCategory.name,
      displayOrder: metricCategory.displayOrder
    },
    metricFamily: {
      id: metricFamily?.metricFamily,
      name: metricFamily?.name,
      displayOrder: metricFamily?.displayOrder
    },
    attributionModel: metricFieldDetails?.metricAttrModel
  };
};

/**
 * Builds Family based on given Attribute.
 * Creates new Family with attribute if family doesn't exist already.
 * In case of empty family, builds family from attribute.
 * In case of existing family, adds attribute to family.
 */
const getFamilyWithAttribute = (
  FamiliesById: Map<string, ColumnFamily>,
  columnFamily: BaseFamily,
  category: string,
  attribute: ColumnAttribute
) => {
  // in case of empty family, fallback to column attribute
  if (!columnFamily || !columnFamily.id || !columnFamily.id) {
    return {
      id: attribute.id,
      name: attribute.name,
      category: category,
      attributes: null,
      displayOrder: attribute.displayOrder || DEFAULT_DISPLAY_ORDER
    };
  }

  const family = FamiliesById.get(columnFamily.id);

  // build from scratch if family doesn't exist in FamiliesById
  if (!family) {
    return {
      id: columnFamily.id || attribute.id,
      name: columnFamily.name || attribute.name,
      category: category,
      attributes: [attribute],
      displayOrder: columnFamily.displayOrder || attribute.displayOrder || DEFAULT_DISPLAY_ORDER
    };
  }

  family.attributes?.push(attribute);
  return family;
};

const getCompatibilityFieldIds = (reportingField: ReportingField, compatibilityType: CompatibilityType) => {
  if (!reportingField.fieldCompatibility) {
    return [];
  }

  return reportingField.fieldCompatibility
    .filter(compatability => compatability.compatibilityType === compatibilityType)
    .flatMap(compatability => compatability.reportingFieldIds);
};

const sortCategories = (categoriesById: Map<string, Category>) =>
  Array.from(categoriesById.values()).sort((a: Category, b: Category) => a.displayOrder - b.displayOrder);

const sortFamiliesAndAttributes = (familiesById: Map<string, ColumnFamily>) => {
  const sortedFamilies = Array.from(familiesById.values()).sort((a: ColumnFamily, b: ColumnFamily) => a.displayOrder - b.displayOrder);
  sortedFamilies.forEach(family => {
    if (family.attributes) {
      family.attributes = [...family.attributes].sort((a: ColumnAttribute, b: ColumnAttribute) => a.displayOrder - b.displayOrder);
    }
  });
  return sortedFamilies;
};

/**
 * ToDo: Add metric metadata - https://i.amazon.com/issues/RCMRX-10868
 * ToDo: Add validation for invalid reporting fields - https://i.amazon.com/issues/RCMRX-11052
 */
export const transformFromApiResponse = (reportingFields: ReportingField[]): FlexibleMetadata => {
  const dimCategoriesById: Map<string, Category> = new Map<string, Category>();
  const dimFamiliesById: Map<string, ColumnFamily> = new Map<string, ColumnFamily>();

  const metricCategoriesById: Map<string, Category> = new Map<string, Category>();
  const metricFamiliesById: Map<string, ColumnFamily> = new Map<string, ColumnFamily>();

  const inCompatibleFields: InCompatibleField[] = [];
  const baseRequiredFields: Map<string, string[]> = new Map<string, string[]>();
  const complimentaryFieldsMap: ComplimentaryFieldMap = {};
  const fieldNamesById: Map<string, string> = new Map<string, string>();

  reportingFields.forEach(reportingField => {
    const attribute: ColumnAttribute = {
      id: reportingField.reportingFieldId,
      name: reportingField.name,
      tooltip: reportingField.definition,
      displayOrder: reportingField.displayOrder ?? 99999,
      isFilterable: reportingField.isFilterable,
      dataType: reportingField.dataType,
      dataRetentionPeriod: undefined,
      enumValues: undefined,
      classifications: reportingField.classifications
    };

    if (reportingField.type === FieldType.DIMENSION_ATTRIBUTE) {
      const { dimCategory, dimFamily, enumValues, dataRetentionPeriod } = getDimDetails(
        reportingField.fieldDetails.dimensionFieldDetails as DimensionFieldDetails
      );
      attribute.dataRetentionPeriod = dataRetentionPeriod;
      attribute.enumValues = enumValues;

      if (!dimCategoriesById.has(dimCategory.id)) {
        dimCategoriesById.set(dimCategory.id, dimCategory);
      }

      const dimFamilyWithAttributes = getFamilyWithAttribute(dimFamiliesById, dimFamily, dimCategory.id, attribute);
      dimFamilyWithAttributes && dimFamiliesById.set(dimFamily.id, dimFamilyWithAttributes);
    }

    if (reportingField.type === FieldType.METRIC) {
      const { metricCategory, metricFamily, attributionModel } = getMetricDetails(
        reportingField.fieldDetails.metricFieldDetails as MetricFieldDetails
      );

      if (!metricCategoriesById.has(metricCategory.id)) {
        metricCategoriesById.set(metricCategory.id, metricCategory);
      }

      if (attributionModel !== AttributionModel.MULTI_TOUCH_LEGACY && attributionModel !== AttributionModel.MULTI_TOUCH) {
        const metricFamilyWithAttributes = getFamilyWithAttribute(metricFamiliesById, metricFamily, metricCategory.id, attribute);
        metricFamilyWithAttributes && metricFamiliesById.set(metricFamilyWithAttributes.id, metricFamilyWithAttributes);
      }
    }

    fieldNamesById.set(reportingField.reportingFieldId, reportingField.name);
    const inCompatibleFieldIds = getCompatibilityFieldIds(reportingField, CompatibilityType.INCOMPATIBLE);
    if (inCompatibleFieldIds.length > 0) {
      inCompatibleFields.push({
        id: reportingField.reportingFieldId,
        incompatibleWith: inCompatibleFieldIds
      });
    }

    const requiredFieldIds = getCompatibilityFieldIds(reportingField, CompatibilityType.REQUIRED);
    if (requiredFieldIds.length > 0) {
      baseRequiredFields.set(reportingField.reportingFieldId, requiredFieldIds);
    }

    const complimentaryFieldIds = getCompatibilityFieldIds(reportingField, CompatibilityType.COMPLEMENTARY);
    complimentaryFieldsMap[reportingField.reportingFieldId] = {
      complimentaryFields: complimentaryFieldIds,
      classifications: reportingField.classifications
    };
  });

  const sortedDimCategories = sortCategories(dimCategoriesById);
  const sortedDimFamilies = sortFamiliesAndAttributes(dimFamiliesById);

  const sortedMetricCategories = sortCategories(metricCategoriesById);
  const sortedMetricFamilies = sortFamiliesAndAttributes(metricFamiliesById);

  const transitiveRequiredFields = Array.from(computeTransitiveFields(baseRequiredFields).entries()).map(([id, fields]) => ({
    id,
    fields
  }));

  return {
    dimCategories: sortedDimCategories,
    dimFamilies: sortedDimFamilies,
    metricCategories: sortedMetricCategories,
    metricFamilies: sortedMetricFamilies,
    inCompatibleFields,
    requiredFields: transitiveRequiredFields,
    complimentaryFieldsMap,
    fieldNamesById: Object.fromEntries(fieldNamesById)
  };
};
